<template>
  <div>
    <v-card class="mb-4">
      <v-card-title>
        <v-icon left
                color="primary">
          fas fa-fw fa-user
        </v-icon>
        {{ isCurrentUser ? 'My Information' : 'Basic Information' }}
      </v-card-title>

      <v-card-text>

        <v-row>
          <v-col cols="12"
                 sm="4">
            <v-text-field v-model="form.first_name"
                          label="First name"
                          outlined
                          dense
                          placeholder=" "
                          autocomplete="off"
                          :rules="[rules.required]"
                          :error-messages="form.errors.get('first_name')">
            </v-text-field>
          </v-col>

          <v-col cols="12"
                 sm="4">
            <v-text-field v-model="form.last_name"
                          label="Last name"
                          outlined
                          dense
                          placeholder=" "
                          autocomplete="off"
                          :rules="[rules.required]"
                          :error-messages="form.errors.get('last_name')">
            </v-text-field>
          </v-col>

          <v-col cols="12"
                 sm="4">
            <v-text-field v-model="form.suffix"
                          label="Suffix"
                          outlined
                          dense
                          placeholder=" "
                          autocomplete="off"
                          :error-messages="form.errors.get('suffix')">
            </v-text-field>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12"
                 sm="4">
            <v-text-field v-model="form.email"
                          type="email"
                          label="Email"
                          outlined
                          dense
                          placeholder=" "
                          autocomplete="off"
                          :rules="[rules.required, rules.email]"
                          :error-messages="form.errors.get('email')"
                          :disabled="isMember"
                          :persistent-hint="isMember"
                          :hint="isMember ? 'Contact support to change your email address.' : ''">
            </v-text-field>
          </v-col>

          <v-col cols="12"
                 sm="4">
            <v-text-field v-model="form.phone"
                          type="tel"
                          label="Mobile Phone"
                          outlined
                          dense
                          placeholder="Optional"
                          autocomplete="off"
                          clearable
                          :v-mask="form.country === 'US' ? '(###)-###-####' : ''"
                          :error-messages="form.errors.get('phone')">
            </v-text-field>
          </v-col>
          <v-col cols="12"
                 sm="4">
            <v-checkbox v-model="form.sms_notifications_enabled"
                        dense
                        :disabled="!form.phone"
                        label="Receive SMS notifications">
            </v-checkbox>
          </v-col>
          <v-col v-if="1===2"
                 cols="12"
                 sm="3">
            <v-text-field v-model="form.phone_extension"
                          label="Phone Extension"
                          outlined
                          dense
                          placeholder="Optional"
                          autocomplete="off"
                          clearable>
            </v-text-field>
          </v-col>

        </v-row>

        <v-row v-if="1===2 && isAdmin">
          <v-col cols="12"
                 sm="4">
            <v-select v-model="form.role"
                      label="Role"
                      outlined
                      dense
                      placeholder=" "
                      :disabled="!isAdmin"
                      :items="teamRoles"
                      item-value="id"
                      item-text="name"
                      :rules="[rules.required]"
                      :error-messages="form.errors.get('role')">
            </v-select>
          </v-col>

          <v-col cols="12"
                 sm="8">
            <v-autocomplete v-model="form.groups"
                            label="Groups"
                            outlined
                            dense
                            placeholder=" "
                            :disabled="!isAdmin"
                            :items="groups"
                            item-value="id"
                            item-text="name"
                            multiple
                            chips
                            deletable-chips
                            small-chips
                            :error-messages="form.errors.get('groups')">
            </v-autocomplete>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12"
                 sm="4">
            <v-select v-model="form.locale"
                      label="Language"
                      outlined
                      dense
                      placeholder=" "
                      :items="locales"
                      item-value="id"
                      item-text="name"
                      :rules="[rules.required]"
                      :error-messages="form.errors.get('locale')">
            </v-select>
          </v-col>

          <v-col cols="12"
                 sm="4">
            <v-autocomplete v-model="form.country"
                            label="Country"
                            outlined
                            dense
                            placeholder=" "
                            autocomplete="off"
                            :items="countries"
                            item-value="alpha2"
                            item-text="name"
                            :rules="[rules.required]"
                            :error-messages="form.errors.get('country')">
            </v-autocomplete>
          </v-col>

          <v-col cols="12"
                 sm="4">
            <v-autocomplete v-model="form.timezone"
                            label="Time Zone"
                            outlined
                            dense
                            placeholder=" "
                            autocomplete="off"
                            :items="timezones"
                            item-value="id"
                            item-text="name"
                            :rules="[rules.required]"
                            :error-messages="form.errors.get('timezone')">
            </v-autocomplete>
          </v-col>
        </v-row>

      </v-card-text>
    </v-card>

    <v-card class="mb-4">
      <v-card-title>
        <v-icon left
                color="primary">
          fas fa-fw fa-briefcase
        </v-icon>
        {{ isCurrentUser ? 'My Job Information' : 'Job Information' }}
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12"
                 sm="4">
            <v-text-field v-model="form.title"
                          label="Title"
                          outlined
                          dense
                          placeholder="Optional"
                          autocomplete="off"
                          clearable
                          :error-messages="form.errors.get('title')">
            </v-text-field>
          </v-col>

          <v-col cols="12"
                 sm="4">
            <v-text-field v-model="form.external_id"
                          label="Employee ID"
                          outlined
                          dense
                          placeholder="Optional"
                          autocomplete="off"
                          clearable
                          :error-messages="form.errors.get('external_id')"
                          :disabled="isMember"
                          :persistent-hint="isMember"
                          :hint="isMember ? 'Contact support to change your employee ID.' : ''">
            </v-text-field>
          </v-col>

          <v-col cols="12"
                 sm="4">
            <v-autocomplete v-model="form.manager_id"
                            label="Manager"
                            outlined
                            dense
                            :items="activeUsers"
                            item-value="id"
                            item-text="name"
                            :item-disabled="isManagerDisabled"
                            placeholder="Optional"
                            autocomplete="off"
                            clearable
                            :disabled="isMember"
                            :persistent-hint="isMember"
                            :hint="isMember ? 'Contact support to change your manager.' : ''"
                            :error-messages="form.errors.get('manager_id')">
            </v-autocomplete>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12"
                 sm="4">
            <v-text-field v-model="form.region"
                          label="Region"
                          outlined
                          dense
                          placeholder="Optional"
                          clearable
                          :disabled="isMember"
                          :persistent-hint="isMember"
                          :hint="isMember ? 'Contact support to change your region.' : ''"
                          :error-messages="form.errors.get('region')">
            </v-text-field>
          </v-col>

          <v-col cols="12"
                 sm="4">
            <v-text-field v-model="form.district"
                          label="District"
                          outlined
                          dense
                          placeholder="Optional"
                          clearable
                          :disabled="isMember"
                          :persistent-hint="isMember"
                          :hint="isMember ? 'Contact support to change your district.' : ''"
                          :error-messages="form.errors.get('district')">
            </v-text-field>
          </v-col>

          <v-col cols="12"
                 sm="4">
            <v-text-field v-model="form.territory"
                          label="Territory"
                          outlined
                          dense
                          placeholder="Optional"
                          clearable
                          :disabled="isMember"
                          :persistent-hint="isMember"
                          :hint="isMember ? 'Contact support to change your territory.' : ''"
                          :error-messages="form.errors.get('territory')">
            </v-text-field>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12"
                 sm="4">
            <v-text-field v-model="form.division"
                          label="Division"
                          outlined
                          dense
                          placeholder="Optional"
                          clearable
                          :disabled="isMember"
                          :persistent-hint="isMember"
                          :hint="isMember ? 'Contact support to change your division.' : ''"
                          :error-messages="form.errors.get('division')">
            </v-text-field>
          </v-col>

          <v-col cols="12"
                 sm="4">
            <v-text-field v-model="form.department"
                          label="Department"
                          outlined
                          dense
                          placeholder="Optional"
                          clearable
                          :disabled="isMember"
                          :persistent-hint="isMember"
                          :hint="isMember ? 'Contact support to change your department.' : ''"
                          :error-messages="form.errors.get('department')">
            </v-text-field>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card v-if="team?.user_photos_enabled"
            class="mb-4">
      <v-card-title>
        <v-icon left
                color="primary">
          fas fa-fw fa-camera
        </v-icon>
        {{ isCurrentUser ? 'My Photo' : 'Photo' }}
      </v-card-title>
      <v-card-text>
        <p v-if="isCurrentUser">
          Upload your photo to personalize your email signature.
        </p>
        <div class="d-flex align-start">
          <div v-if="displayPhotoURL"
               class="mr-4"
               style="width: 60px;">
            <v-img :src="displayPhotoURL"
                   alt="Headshot photo"
                   height="60"
                   contain>
            </v-img>
            <a v-if="user.photo_url && (photoForm.busy === false && form.busy === false)"
               @click.prevent="deletePhoto"
               class="d-block text-center">
              Delete
            </a>
          </div>
          <div class="flex-grow-1">
            <v-file-input v-model="photoForm.photo"
                          label="Upload Photo"
                          :disabled="photoForm.busy || form.busy"
                          placeholder="Optional"
                          clearable
                          show-size
                          outlined
                          dense
                          hint="This should be a PNG or JPG"
                          persistent-hint
                          prepend-icon=""
                          :truncate-length="200"
                          accept="image/jpg,image/png"
                          :error-messages="photoForm.errors.get('photo')"
                          class="mb-4">
            </v-file-input>

            <v-text-field v-if="1===2"
                          v-model="form.photo_url"
                          type="url"
                          outlined
                          dense
                          label="Photo URL"
                          placeholder="Optional"
                          clearable
                          :error-messages="form.errors.get('photo_url')">
            </v-text-field>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <v-card class="mb-4">
      <v-card-title>
        <v-icon left
                color="primary">
          fas fa-fw fa-database
        </v-icon>
        {{ isCurrentUser ? 'My CRM Settings' : 'CRM Settings' }}
      </v-card-title>
      <v-card-text>

        <template v-if="team.use_crm_mailbox">
          <v-text-field v-model="form.crm_mailbox"
                        type="email"
                        outlined
                        dense
                        :label="crmMailboxLabel"
                        placeholder="Optional"
                        clearable
                        :error-messages="form.errors.get('crm_mailbox')"
                        :hide-details="!form.errors.has('crm_mailbox')"
                        :class="form.errors.has('crm_mailbox') ? 'mb-4' : 'mb-0'">
          </v-text-field>
          <div v-if="team.uses_salesforce"
               class="text-caption mb-6">
            Read <a href="https://help.mobilelocker.com/article/337-how-to-configure-my-email-to-salesforce" target="_blank">this article</a> or contact Mobile Locker support.
          </div>
        </template>

        <v-text-field v-if="1===2 && team.sso_enabled"
                      v-model="form.sso_username"
                      label="SSO Username"
                      outlined
                      dense
                      placeholder="Only fill this in if instructed by Mobile Locker support."
                      clearable
                      :error-messages="form.errors.get('sso_username')">
        </v-text-field>

      </v-card-text>
    </v-card>

    <v-card v-if="team.calendar_enabled"
            class="mb-4">
      <v-card-title>
        <v-icon left
                color="primary">
          fas fa-fw fa-calendar-alt
        </v-icon>
        Calendar Settings
      </v-card-title>
      <v-card-text>

        <v-text-field v-model="form.calendar_url"
                      type="url"
                      outlined
                      dense
                      label="Calendar URL"
                      placeholder="Optional"
                      clearable
                      :error-messages="form.errors.get('calendar_url')">
        </v-text-field>
      </v-card-text>
    </v-card>
  </div>
</template>
<script>
import {mapGetters} from 'vuex';
import formValidation from '../../../plugins/formValidation';
import userService from '../../../services/userService';

export default {
  mixins: [formValidation],
  props: {
    form: {
      type: Object,
      required: true,
    },
    photoForm: { // MLD-7821
      type: Object,
      required: true,
    },
    user: {
      type: Object,
      required: false,
    }
  },
  data() {
    return {}
  },
  computed: {
    ...mapGetters([
      'activeUsers',
      'crmMailboxLabel',
      'countries',
      'currentUser',
      'groups',
      'isAdmin',
      'isMember',
      'locales',
      'team',
      'teamRoles',
      'timezones',
    ]),
    displayPhotoURL() {
      // Priority: uploaded file > form.photo_url > user.photo_url
      if (this.photoForm.photo) {
        return URL.createObjectURL(this.photoForm.photo);
      }

      if (this.form.photo_url) {
        return this.form.photo_url;
      }

      return null;
    },
    isCurrentUser() {
      return this.currentUser.id === this.user.id;
    },
  },
  methods: {
    isManagerDisabled(item) {
      return this.user && (item.id === this.user.id || item.manager_id === this.user.id)
    },
    async deletePhoto() { // MLD-7821
      if (this.photoForm.busy || this.form.busy) {
        return;
      }
      try {
        this.form.startProcessing();
        await userService.deletePhoto(this.user.id);
        this.form.photo_url = null;
        this.photoForm.photo = null;
        this.$emit('delete-photo');
        this.$toast.success('Photo deleted.');
      } catch (error) {
        this.error = error;
      } finally {
        this.form.busy = false;
      }
    }
  }
}
</script>
